import React, { useEffect, useMemo, useState } from 'react';
import apiConfig from '../config/api';
import LocationMap from './LocationMap';
import AnalyticsService from '../services/AnalyticsService';
import './PlanDetailPage.css';

function decodePayload(raw) {
  if (!raw) return null;
  try {
    return JSON.parse(decodeURIComponent(escape(atob(decodeURIComponent(raw)))));
  } catch {
    return null;
  }
}

function toGeometryArray(rawGeometry) {
  if (!rawGeometry) return null;
  if (typeof rawGeometry === 'string') {
    try {
      return JSON.parse(rawGeometry);
    } catch {
      return null;
    }
  }
  return rawGeometry;
}

function formatDisplayDate(value) {
  if (!value) return '-';
  if (typeof value === 'number') {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString('he-IL');
  }

  const s = String(value).trim();
  if (!s) return '-';

  // Already DD/MM/YYYY or DD.MM.YYYY
  if (/^\d{1,2}[./]\d{1,2}[./]\d{4}$/.test(s)) {
    return s.replace(/\./g, '/');
  }

  const parsed = new Date(s);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleDateString('he-IL');
  }

  return s;
}

function getDomainName(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

function pickFirst(source, keys = []) {
  if (!source || typeof source !== 'object') return null;
  for (const key of keys) {
    const value = source[key];
    if (value === null || value === undefined) continue;
    const text = String(value).trim();
    if (text && text !== '-') return value;
  }
  return null;
}

function formatNumber(value, maximumFractionDigits = 3) {
  if (value === null || value === undefined || value === '') return '-';
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  return num.toLocaleString('he-IL', { maximumFractionDigits });
}

function formatArea(value) {
  if (value === null || value === undefined || value === '') return '-';
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  // Tel Aviv layers often return square meters for large polygons.
  if (num > 1_000_000) {
    return `${(num / 1000).toLocaleString('he-IL', { maximumFractionDigits: 3 })} דונם`;
  }
  return `${num.toLocaleString('he-IL', { maximumFractionDigits: 3 })} דונם`;
}

function normalizeObjectivesText(value) {
  const text = String(value || '')
    .replace(/\s*\^\s*/g, '\n')
    .replace(/\r\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  if (!text) return '';
  // iPlan frequently truncates this field at 250 chars.
  if (text.length === 250 && !/[.!?…]$/.test(text)) return `${text}…`;
  return text;
}

function isLikelyLowQualityObjectives(text) {
  const raw = String(text || '').trim();
  if (!raw) return true;
  const compact = raw.replace(/\s+/g, '');
  if (!compact) return true;
  const total = compact.length;
  const hebrew = (compact.match(/[\u0590-\u05FF]/g) || []).length;
  const latin = (compact.match(/[A-Za-z]/g) || []).length;
  const replacement = (compact.match(/�/g) || []).length;
  const symbols = (compact.match(/[^\u0590-\u05FFA-Za-z0-9.,:;!?'"()\-/%]/g) || []).length;
  const bidiMarks = (raw.match(/[\u200E\u200F\u202A-\u202E]/g) || []).length;
  const lines = raw.split('\n').map((line) => line.trim()).filter(Boolean);
  const shortLines = lines.filter((line) => line.length <= 3).length;
  const hebrewRatio = hebrew / total;
  const latinRatio = latin / total;
  const noiseRatio = (replacement + symbols) / total;
  const shortLineRatio = lines.length ? shortLines / lines.length : 0;
  return hebrewRatio < 0.25 || latinRatio > 0.2 || noiseRatio > 0.08 || bidiMarks > 5 || (shortLineRatio > 0.28 && lines.length > 10);
}

function friendlyDocLabel(doc) {
  const type = String(doc?.doc_type || '').toLowerCase();
  if (type.includes('municipal')) return 'Open municipal plan documents';
  if (type.includes('permit')) return 'Open permit-related document';
  return 'Open source document';
}

function friendlyLinkLabel(link) {
  const label = String(link?.label || '').toLowerCase();
  if (label.includes('פורטל')) return 'Open Tel Aviv planning portal';
  if (label.includes('מקור')) return 'Open source record page';
  if (label.includes('חלופי')) return 'Open alternative source link';
  return 'Open source link';
}

function getMapCoordinates(display, geometry) {
  const centerFromBbox = () => {
    const minX = Number(display?.bbox_min_x);
    const minY = Number(display?.bbox_min_y);
    const maxX = Number(display?.bbox_max_x);
    const maxY = Number(display?.bbox_max_y);
    if ([minX, minY, maxX, maxY].every(Number.isFinite)) {
      return { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };
    }
    return null;
  };

  const centerFromGeometry = () => {
    if (!geometry) return null;
    let pts = [];
    if (Array.isArray(geometry.rings)) pts = geometry.rings.flat();
    else if (Array.isArray(geometry.paths)) pts = geometry.paths.flat();
    else if (geometry.x !== undefined && geometry.y !== undefined) return { x: Number(geometry.x), y: Number(geometry.y) };
    if (!pts.length) return null;
    const good = pts.filter((p) => Array.isArray(p) && Number.isFinite(Number(p[0])) && Number.isFinite(Number(p[1])));
    if (!good.length) return null;
    const sum = good.reduce((acc, p) => ({ x: acc.x + Number(p[0]), y: acc.y + Number(p[1]) }), { x: 0, y: 0 });
    return { x: sum.x / good.length, y: sum.y / good.length };
  };

  return centerFromBbox() || centerFromGeometry();
}

const PlanDetailPage = () => {
  const [record, setRecord] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loadingRecord, setLoadingRecord] = useState(true);
  const [links, setLinks] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(false);

  const pathId = useMemo(() => {
    const parts = window.location.pathname.split('/').filter(Boolean);
    // Preserve slashes inside plan ids (e.g. תמ"א/60) by joining all path parts after `/plans`.
    const raw = parts.slice(1).join('/') || '';
    try {
      return decodeURIComponent(raw).replace(/\s*\/\s*/g, '/').replace(/\s+/g, ' ').trim();
    } catch {
      return raw.replace(/\s*\/\s*/g, '/').replace(/\s+/g, ' ').trim();
    }
  }, []);

  const payload = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return decodePayload(params.get('data'));
  }, []);

  useEffect(() => {
    if (!pathId) return;
    AnalyticsService.trackPlanDetailOpen({
      planNumber: pathId,
      title: payload?.title,
      municipality: payload?.municipality || payload?.county,
      sourceSurface: 'plan_detail_page',
    });
  }, [pathId, payload]);

  const shouldQueryTaRecord = useMemo(() => Boolean(pathId), [pathId]);

  useEffect(() => {
    const loadRecord = async () => {
      if (!pathId || !shouldQueryTaRecord) {
        setLoadingRecord(false);
        return;
      }
      setLoadingRecord(true);
      try {
        const idCandidates = [
          pathId,
          String(payload?.plan_number || payload?.planNumber || '').trim(),
        ]
          .map((v) => String(v || '').replace(/\s*\/\s*/g, '/').replace(/\s+/g, ' ').trim())
          .filter(Boolean)
          .filter((v, i, arr) => arr.indexOf(v) === i);

        let found = null;
        const titleParam = encodeURIComponent(String(payload?.title || ''));
        for (const candidateId of idCandidates) {
          const res = await fetch(
            `${apiConfig.apiBaseUrl}/api/ta-records?kind=plan&id=${encodeURIComponent(candidateId)}&title=${titleParam}`
          );
          if (!res.ok) continue;
          const data = await res.json();
          if (data?.record) {
            found = data;
            break;
          }
        }

        if (!found) return;
        setRecord(found.record || null);
        setDocuments(Array.isArray(found.documents) ? found.documents : []);
        if (Array.isArray(found.links) && found.links.length > 0) setLinks(found.links);
      } catch {
        setRecord(null);
        setDocuments([]);
      } finally {
        setLoadingRecord(false);
      }
    };
    loadRecord();
  }, [pathId, payload, shouldQueryTaRecord]);

  useEffect(() => {
    const resolveLinks = async () => {
      const source = record || payload;
      if (!source || links.length > 0) return;
      setLoadingLinks(true);
      try {
        const params = new URLSearchParams({
          url: source.plan_url || source.planUrl || '',
          municipality: source.municipality || source.county || '',
          source: source.dataSource || source.source || 'tel_aviv_municipal',
          planNumber: source.plan_number || source.planNumber || pathId || '',
          planTitle: source.title || '',
        });
        const res = await fetch(`${apiConfig.apiBaseUrl}/api/plan-links?${params.toString()}`);
        const data = await res.json();
        setLinks(data.links || []);
      } catch {
        setLinks([]);
      } finally {
        setLoadingLinks(false);
      }
    };
    resolveLinks();
  }, [payload, record, links.length, pathId]);

  const display = record || payload;
  const geometry = toGeometryArray(display?.geometry_json || display?.geometryJson || null);
  const rawAttributes = useMemo(() => {
    const attrs = display?.raw?.attributes;
    return attrs && typeof attrs === 'object' ? attrs : {};
  }, [display]);
  const mapCoordinates = useMemo(() => getMapCoordinates(display, geometry), [display, geometry]);
  const documentUrls = useMemo(() => new Set(documents.map((d) => d.doc_url).filter(Boolean)), [documents]);
  const visibleLinks = useMemo(() => {
    const out = [];
    const seen = new Set();
    for (const link of links || []) {
      if (!link?.available || !link?.url) continue;
      if (documentUrls.has(link.url)) continue;
      if (seen.has(link.url)) continue;
      seen.add(link.url);
      out.push(link);
    }
    return out;
  }, [links, documentUrls]);

  const goBack = () => {
    if (window.history.length > 1) {
      window.history.back();
      return;
    }
    window.history.pushState({}, '', '/');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  if (!display && !loadingRecord) {
    return (
      <div className="plan-detail-page" dir="rtl">
        <div className="plan-detail-shell">
          <h1 className="plan-title">פרטי תוכנית</h1>
          <p>לא נמצאו פרטי תוכנית לשיתוף הזה.</p>
          <button className="plan-ghost-btn" onClick={goBack}>חזרה</button>
        </div>
      </div>
    );
  }

  const headerNumber = display?.plan_number || display?.planNumber || pathId;
  const objectivesCandidate = normalizeObjectivesText(
    display?.mavat_objectives_full ||
    display?.mavatObjectivesFull ||
    display?.iplan_objectives ||
    display?.objectives ||
    pickFirst(rawAttributes, [
      'mtrot_toknniot',
      'tochnit_matarot',
      'matarot',
      'tochen',
      'מטרות',
      'מטרות התוכנית',
    ])
  );
  const objectivesText = isLikelyLowQualityObjectives(objectivesCandidate) ? '' : objectivesCandidate;
  const rows = [
    ['סטטוס', display?.status || '-'],
    ['סוג תוכנית', display?.plan_type || display?.planType || '-'],
    ['תת סוג', display?.plan_subtype || display?.planSubtype || '-'],
    ['רשות', display?.municipality || display?.county || 'תל אביב-יפו'],
    ['תאריך הפקדה', formatDisplayDate(display?.submission_date || display?.submissionDate)],
    ['תאריך אישור', formatDisplayDate(display?.approval_date || display?.approvalDate)],
    ['סמכות מאשרת', display?.approving_authority || display?.approvingAuthority || '-'],
    ['ניתן להוציא היתרים', display?.can_issue_permits === true ? 'כן' : display?.can_issue_permits === false ? 'לא' : '-'],
  ];
  const additionalRows = [
    ['מספר תוכנית במבאת', pickFirst(rawAttributes, ['mispar_tochnit_mavat', 'id_taba']) || '-'],
    ['היקף', display?.location || pickFirst(rawAttributes, ['t_hekef', 'k_heykef']) || '-'],
    ['סיווג', display?.plan_subtype || pickFirst(rawAttributes, ['t_sivug', 'k_sivug']) || '-'],
    ['מסלול', display?.approving_authority || pickFirst(rawAttributes, ['maslul', 'k_maslul']) || '-'],
    ['סוג נושא', pickFirst(rawAttributes, ['sug_nose', 'k_sug_nose']) || '-'],
    ['שטח גרפי', formatArea(pickFirst(rawAttributes, ['ms_shetach_graphi', 'Shape_Area', 'shape_area']))],
    ['שטח (ערך מקור)', formatNumber(pickFirst(rawAttributes, ['ms_shetach', 'area']))],
    ['ילקוט אחרון', pickFirst(rawAttributes, ['ms_yalkut_acharon']) || '-'],
    ['תאריך שינוי סטטוס', formatDisplayDate(pickFirst(rawAttributes, ['tr_shinuy_status']))],
  ].filter(([, value]) => value && value !== '-');

  return (
    <div className="plan-detail-page" dir="rtl">
      <div className="plan-detail-shell">
        <div className="plan-topbar">
          <button className="plan-ghost-btn" onClick={goBack}>חזרה לתוצאות</button>
          <div className="plan-meta-note">{display?.dataSource || display?.source || 'מאגר עירוני תל אביב'}</div>
        </div>

        <div className="plan-hero">
          <div>
            <div className="plan-number">תוכנית #{headerNumber}</div>
            <h1 className="plan-title">{display?.title || 'ללא שם תוכנית'}</h1>
          </div>
          <div className="plan-actions">
            {loadingLinks ? <span className="plan-muted">בודק קישורים...</span> : null}
            {visibleLinks.slice(0, 2).map((link) => (
              <button
                key={link.url}
                className="plan-primary-btn"
                onClick={() => window.open(link.url, '_blank', 'noopener,noreferrer')}
              >
                {link.label}
              </button>
            ))}
          </div>
        </div>

        <div className="plan-grid">
          <section className="plan-card plan-info-card">
            <h2>פרטי תוכנית</h2>
            {loadingRecord ? <p className="plan-muted">טוען נתונים...</p> : null}
            <div className="plan-info-grid">
              {rows.map(([label, value]) => (
                <React.Fragment key={label}>
                  <div className="plan-label">{label}</div>
                  <div className="plan-value">{value || '-'}</div>
                </React.Fragment>
              ))}
            </div>
          </section>

          <section className="plan-card plan-map-card">
            <h2>מיקום תחולת התוכנית</h2>
            {loadingRecord ? (
              <div className="plan-map-loading">Loading map...</div>
            ) : mapCoordinates ? (
              <LocationMap coordinates={mapCoordinates} address={display?.title || `Plan ${headerNumber}`} stacked={true} showStreetView={false} geometry={geometry} zoomDelta={-1} />
            ) : (
              <div className="plan-geometry-empty">No map geometry available for this plan yet.</div>
            )}
          </section>

          {additionalRows.length > 0 ? (
            <section className="plan-card plan-additional-card">
              <h2>פרטים נוספים</h2>
              <div className="plan-info-grid">
                {additionalRows.map(([label, value]) => (
                  <React.Fragment key={label}>
                    <div className="plan-label">{label}</div>
                    <div className="plan-value">{value}</div>
                  </React.Fragment>
                ))}
              </div>
            </section>
          ) : null}

          {objectivesText ? (
            <section className="plan-card plan-objectives-card">
              <h2>תוכן ומטרות</h2>
              <p>{objectivesText}</p>
            </section>
          ) : null}

          <section className="plan-card plan-links-card">
            <h2>מסמכים וקישורים</h2>

            {documents.length > 0 ? (
              <div className="plan-link-buttons">
                {documents.map((doc) => (
                  <button
                    key={doc.doc_url}
                    className="plan-secondary-btn"
                    onClick={() => window.open(doc.doc_url, '_blank', 'noopener,noreferrer')}
                    title="Open document"
                  >
                    {friendlyDocLabel(doc)}
                    <span className="plan-link-sub">{getDomainName(doc.doc_url)}</span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="plan-muted">לא נמצאו מסמכים שמורים.</p>
            )}

            <div className="plan-link-buttons">
              {visibleLinks.map((link) => (
                <button
                  key={link.url}
                  className="plan-secondary-btn"
                  onClick={() => window.open(link.url, '_blank', 'noopener,noreferrer')}
                  title="Open source link"
                >
                  {friendlyLinkLabel(link)}
                  <span className="plan-link-sub">{getDomainName(link.url)}</span>
                </button>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PlanDetailPage;
